import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { useClinic } from '@/hooks/use-clinic'
import { useClinicUi } from '@/hooks/use-clinic-ui'
import { formatDate, formatWeekday } from '@/lib/clinic/datetime'
import { Button } from '@/components/ui/button'
import BrandMark from '@/components/generals/brand-mark'

import {
    CalendarClock,
    FileText,
    HardDrive,
    LayoutDashboard,
    Menu,
    Search,
    Settings,
    Stethoscope,
    UserPlus,
    Users
} from 'lucide-react'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

interface AppShellProps {
    children: ReactNode
}

interface NavItem {
    href: string
    label: string
    icon: LucideIcon
}

const NAV_ITEMS: NavItem[] = [
    { href: '/', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/patients', label: 'Patients', icon: Users },
    { href: '/prescriptions', label: 'Prescriptions', icon: FileText },
    { href: '/follow-ups', label: 'Follow-ups', icon: CalendarClock },
    { href: '/settings', label: 'Settings', icon: Settings }
]

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    if (href === '/patients') return pathname.startsWith('/patients') || pathname.startsWith('/consultations')

    return pathname.startsWith(href)
}

interface SidebarContentProps {
    onNavigate?: () => void
}

const SidebarContent: FC<SidebarContentProps> = ({ onNavigate }): ReactNode => {
    const router = useRouter()
    const { data, ready, today } = useClinic()
    const ui = useClinicUi()

    const attentionCount = ready
        ? data.followUps.filter(item => item.status === 'pending' && item.dueDate <= today).length
        : 0

    const run = (action: () => void) => {
        onNavigate?.()
        action()
    }

    return (
        <div className='flex h-full flex-col'>
            <div className='px-5 pt-5 pb-5'>
                <Link
                    href='/'
                    onClick={onNavigate}
                    className='block rounded-lg outline-none focus-visible:ring-3 focus-visible:ring-ring/40'
                >
                    <BrandMark clinicName={data.settings.clinicName} logo={data.settings.logo} />
                </Link>
            </div>

            <div className='flex flex-col gap-2 px-4'>
                <button
                    type='button'
                    onClick={() => run(() => ui.openPatientPicker('open'))}
                    className='flex h-10 items-center gap-2 rounded-lg border border-border bg-muted/60 px-3 text-sm text-muted-foreground transition-colors outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/40'
                >
                    <Search className='size-4' />

                    <span className='flex-1 text-left'>
                        Search patients
                    </span>

                    <kbd className='rounded-md border border-border bg-background px-1.5 py-0.5 font-sans text-[10px] font-semibold text-muted-foreground'>
                        Ctrl K
                    </kbd>
                </button>

                <Button size='lg' className='w-full justify-start' onClick={() => run(() => ui.startConsultation())}>
                    <Stethoscope />
                    Start consultation
                </Button>

                <Button size='lg' variant='outline' className='w-full justify-start' onClick={() => run(() => ui.registerPatient())}>
                    <UserPlus />
                    Register patient
                </Button>
            </div>

            <nav aria-label='Main' className='mt-7 flex-1 px-3'>
                <p className='px-3 pb-2 text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase'>
                    Menu
                </p>

                <ul className='flex flex-col gap-0.5'>
                    {NAV_ITEMS.map(item => {
                        const active = isActive(router.pathname, item.href)

                        return (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    onClick={onNavigate}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/40',
                                        active
                                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                            : 'text-sidebar-foreground hover:bg-muted'
                                    )}
                                >
                                    <item.icon className={cn('size-[18px]', active ? 'text-primary' : 'text-muted-foreground')} />

                                    <span className='flex-1'>
                                        {item.label}
                                    </span>

                                    {item.href === '/follow-ups' && attentionCount > 0 && (
                                        <span
                                            className='min-w-5 rounded-full bg-rose-500 px-1.5 py-0.5 text-center text-[11px] leading-none font-semibold text-white tabular-nums'
                                            aria-label={`${attentionCount} follow-ups due or overdue`}
                                        >
                                            {attentionCount}
                                        </span>
                                    )}
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </nav>

            <div className='border-t border-sidebar-border px-5 py-4 text-xs text-muted-foreground'>
                {ready ? (
                    <p className='font-medium text-foreground tabular-nums'>
                        {formatWeekday(today)}, {formatDate(today)}
                    </p>
                ) : (
                    <span className='block h-4' />
                )}

                <p className='mt-1 flex items-center gap-1.5'>
                    <HardDrive className='size-3.5' />
                    Records are saved on this device
                </p>
            </div>
        </div>
    )
}

const AppShell: FC<AppShellProps> = ({ children }): ReactNode => {
    const [mobileOpen, setMobileOpen] = useState(false)
    const { data } = useClinic()
    const ui = useClinicUi()

    return (
        <div className='min-h-screen bg-muted/40'>
            <a
                href='#main-content'
                className='sr-only z-50 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3'
            >
                Skip to content
            </a>

            <aside className='fixed inset-y-0 left-0 z-30 hidden w-64 overflow-y-auto border-r border-sidebar-border bg-sidebar lg:block'>
                <SidebarContent />
            </aside>

            <header className='sticky top-0 z-40 flex h-14 items-center gap-1.5 border-b border-border bg-background/90 px-2.5 backdrop-blur-md lg:hidden'>
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger render={<Button variant='ghost' size='icon' aria-label='Open menu' />}>
                        <Menu className='size-5' />
                    </SheetTrigger>

                    <SheetContent side='left' className='gap-0 p-0 data-[side=left]:w-[288px]'>
                        <SheetTitle className='sr-only'>
                            Navigation
                        </SheetTitle>

                        <SheetDescription className='sr-only'>
                            Main menu and quick actions
                        </SheetDescription>

                        <SidebarContent onNavigate={() => setMobileOpen(false)} />
                    </SheetContent>
                </Sheet>

                <Link href='/' className='min-w-0 flex-1 rounded-lg outline-none focus-visible:ring-3 focus-visible:ring-ring/40'>
                    <BrandMark clinicName={data.settings.clinicName} logo={data.settings.logo} compact />
                </Link>

                <Button variant='ghost' size='icon' aria-label='Search patients' onClick={() => ui.openPatientPicker('open')}>
                    <Search className='size-5' />
                </Button>

                <Button size='icon' aria-label='Start consultation' onClick={() => ui.startConsultation()}>
                    <Stethoscope className='size-5' />
                </Button>
            </header>

            <main id='main-content' className='lg:pl-64'>
                {children}
            </main>
        </div>
    )
}

export default AppShell
